import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, Stack } from "@mui/material";
import { SHIFT_DISCOVERY_WORKPLACE_PROFILE_FULL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import {
  BottomSheet,
  BottomSheetIllustrationContent,
  List,
  ListItem,
  ListItemButton,
  ListItemIconSkeleton,
  ListItemText,
  ListItemTextSkeleton,
  LoadMoreTrigger,
  Text,
  Title,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { BottomSheetHeader } from "../../../components/BottomSheetHeader";
import { useGetWorkplacesById } from "../../../Workplace/useGetWorkplacesById";
import { getApplicableWorkplacesDetails } from "../../utils/getApplicableWorkplacesDetails";
import { getApplicableWorkplacesTextForRequirement } from "../../utils/getApplicableWorkplacesTextForRequirement";
import { getRequirementWorkplacesIds } from "../getRequirementWorkplacesIds";
import { useRequirementDetailsContext } from "../useRequirementDetailsContext";

interface DocumentDetailsRequiredByWorkplacesBottomSheetProps {
  modalState: UseModalState;
}

export function DocumentDetailsRequiredByWorkplacesBottomSheet(
  props: DocumentDetailsRequiredByWorkplacesBottomSheetProps
) {
  const { modalState } = props;
  const history = useHistory();
  const { requirement } = useRequirementDetailsContext();
  const worker = useDefinedWorker();

  const requiredWorkplaces = getRequirementWorkplacesIds(requirement);

  const applicableWorkplacesDetails = getApplicableWorkplacesDetails({
    workerRequirement: requirement,
    workerAddress: worker.address,
    requiredByWorkplacesCount: requiredWorkplaces.length,
  });

  const { data, isLoading, isError, isSuccess, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetWorkplacesById({
      workplaceIds: requiredWorkplaces,
    });

  // Flatten all workplaces from all pages
  const allWorkplaces = data?.pages.flatMap((page) => page.data) ?? [];

  const handleClose = useCallback(() => {
    modalState.closeModal();
  }, [modalState]);

  if (!isDefined(applicableWorkplacesDetails)) {
    return null;
  }

  return (
    <BottomSheet header={<BottomSheetHeader onClose={handleClose} />} modalState={modalState}>
      <Stack spacing={1} sx={{ alignItems: "center", paddingX: 5, paddingY: 7 }}>
        <Title component="h6">Required by</Title>
        <Text variant="caption" sx={(theme) => ({ color: theme.palette.text.secondary })}>
          {getApplicableWorkplacesTextForRequirement(applicableWorkplacesDetails, false)}
        </Text>
      </Stack>

      <DialogContent sx={{ paddingY: 5, paddingX: 7 }}>
        <Stack spacing={3} direction="column">
          <List dense>
            {isLoading &&
              Array.from({ length: 10 })
                .fill(0)
                .map((_, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <ListItem key={index}>
                    <ListItemIconSkeleton />
                    <ListItemTextSkeleton primaryWidth="60%" secondaryWidth={0} />
                  </ListItem>
                ))}

            {isError && (
              <BottomSheetIllustrationContent
                illustrationType="error"
                title="Error loading workplaces"
                description="Please try again later"
              />
            )}

            {isSuccess &&
              allWorkplaces.map((workplace) => (
                <ListItem key={workplace.id}>
                  <ListItemButton
                    onClick={() => {
                      const workplaceDetailsPath = generatePath(
                        SHIFT_DISCOVERY_WORKPLACE_PROFILE_FULL_PATH,
                        {
                          workplaceId: workplace.id,
                        }
                      );
                      history.push(workplaceDetailsPath);
                    }}
                  >
                    <ListItemText>{workplace.attributes.name}</ListItemText>
                  </ListItemButton>
                </ListItem>
              ))}

            <LoadMoreTrigger
              canLoadMore={hasNextPage}
              isLoading={isFetchingNextPage}
              onLoadMore={async () => {
                await fetchNextPage();
              }}
            />
          </List>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
